import { Box, Text } from 'grommet';
import { Children, Fragment, isValidElement, ReactElement, ReactNode } from 'react';
import { Recalculating } from './estimate/Recalculating';
import { SaveStatus, SaveStatusIndicator } from './SaveStatusIndicator';

/** A Box's gap only spaces its direct children, so a fragment's children are spread into the list */
const items = (node: ReactNode) =>
  Children.toArray(node).flatMap((child) =>
    isValidElement(child) && child.type === Fragment
      ? Children.toArray((child as ReactElement<{ children?: ReactNode }>).props.children)
      : [child]
  );

export type PageHeaderProps = {
  /** The page's name; a node for one that can be edited, like a project's */
  title: ReactNode;
  saveStatus?: SaveStatus;
  /** Beside the title: the page's main actions */
  actions?: ReactNode;
  /** On the right: the page's totals (HeaderTotal) */
  totals?: ReactNode;
};

/** The white band at the top of a page: its title and save status, actions, and totals */
export const PageHeader = ({ title, saveStatus, actions, totals }: PageHeaderProps) => (
  <Box
    background="white"
    border={{ side: 'bottom', color: 'border-layout' }}
    pad={{ vertical: '16px', horizontal: 'medium' }}
    direction="row"
    wrap
    justify="between"
    align="center"
    gap="medium"
    flex={false}
  >
    <Box direction="row" align="center" gap="large" wrap>
      <Box gap="xxsmall">
        {typeof title === 'string' ? (
          <Text size="22px" weight={600} color="text-heading">
            {title}
          </Text>
        ) : (
          title
        )}
        <SaveStatusIndicator saveStatus={saveStatus} boxProps={{ margin: 'none' }} />
      </Box>
      {actions && (
        <Box direction="row" gap="small" align="center" wrap>
          {items(actions)}
        </Box>
      )}
    </Box>
    {totals && (
      <Box direction="row" gap="large" align="end" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {items(totals)}
      </Box>
    )}
  </Box>
);

export type HeaderTotalProps = {
  label: string;
  value: string;
  busy?: boolean;
  /** The grand total: bigger, in the brand color */
  grand?: boolean;
};

/** One of a page's totals: a small grey label over the amount */
export const HeaderTotal = ({ label, value, busy = false, grand }: HeaderTotalProps) => (
  <Box gap="xxsmall" align="end">
    <Text size="xsmall" color="text-label">
      {label}
    </Text>
    <Recalculating busy={busy}>
      <Text size={grand ? '24px' : '18px'} weight={600} color={grand ? 'brand' : 'text-heading'}>
        {value}
      </Text>
    </Recalculating>
  </Box>
);
